import React from 'react';
import styled from 'styled-components';
import { CardRow } from '../../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../../generic/ui/Mono';
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon';
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon';
import ProductImage from '../../../queries/TeudotDetails/Components/ProductImage';
import { IPalletDetails } from '../../../../../../modules/API/Pallets/models/palletDetails';

interface Props {
    item: IPalletDetails;
    isLast?: boolean;
    onRemark: (item: IPalletDetails) => void;
}

const CheckPalletRow: React.FC<Props> = ({ item, isLast = false, onRemark }) => {
    const {
        ProductDesc,
        VarietyDesc,
        SizeID,
        Pack_Name,
        Quantity,
        WeightNeto,
        GrowerName,
        GeneralProductID,
    } = item;

    const productName = `${ProductDesc?.trim() ?? ''} ${VarietyDesc?.trim() ?? ''} ${SizeID?.trim() ?? ''}`.trim();

    return (
        <CardRow
            $columns='2.75rem minmax(0, 1fr) auto 2.75rem'
            $mobileColumns='2.5rem minmax(0, 1fr) auto 2.5rem'
            $isLast={isLast}
            $hover={false}
        >
            <CardRow.Cell $align='center'>
                <ImageSlot>
                    <ProductImage id={GeneralProductID} />
                </ImageSlot>
            </CardRow.Cell>

            <CardRow.Cell>
                <CardRow.Stack $gap='0.25rem'>
                    <Name>{productName}</Name>
                    {GrowerName && (
                        <MetaItem>
                            <MetaIcon>
                                <UserIcon />
                            </MetaIcon>
                            <MetaText>{GrowerName.trim()}</MetaText>
                        </MetaItem>
                    )}
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <CardRow.Stack $gap='0.2rem' $align='end'>
                    {Pack_Name && (
                        <MetaItem>
                            <MetaIcon>
                                <PackageBoxIcon />
                            </MetaIcon>
                            <MetaText>
                                {Pack_Name.trim()}
                                {' · '}
                                <Mono>{Quantity}</Mono>
                            </MetaText>
                        </MetaItem>
                    )}
                    <WeightLine>
                        <MetaIcon>
                            <ScaleIcon />
                        </MetaIcon>
                        <Mono>{typeof WeightNeto === 'number' ? WeightNeto.toFixed(1) : WeightNeto}</Mono>
                    </WeightLine>
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <RemarkBtn type='button' onClick={() => onRemark(item)} aria-label='הערת ביקורת משטח'>
                    <PencilIcon />
                </RemarkBtn>
            </CardRow.Cell>
        </CardRow>
    );
};

export default CheckPalletRow;

const ImageSlot = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: ${({ theme }) => theme.radii.sm};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.25rem;
        height: 2.25rem;
    }
`;

const Name = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: ${({ theme }) => theme.colors.ink3};
`;

const MetaIcon = styled.span`
    color: ${({ theme }) => theme.colors.icon};
    display: inline-flex;
    align-items: center;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const MetaText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const WeightLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
`;

const RemarkBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    flex-shrink: 0;
    border-radius: 0.5rem;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1.05rem;
        height: 1.05rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentDeep};
        border-color: ${({ theme }) => theme.colors.accentDeep};
    }

    &:active {
        transform: scale(0.94);
    }
`;
